/**
 * 统一 SSE 客户端 -- 提取自 taskRuntime.ts / useSSE.ts / useConsoleStream.ts 的最佳实践
 *
 * 特性：
 * - fetch-based SSE（支持 POST + 自定义 header，不依赖原生 EventSource）
 * - 指数退避重连（默认 max 5 次，可配置）
 * - AbortController 生命周期管理
 * - Authorization header 传递 token（不走 URL query，防泄漏）
 * - Last-Event-ID 断点续传
 * - SSE 协议完整解析（id / event / data / 注释行）
 * - 主动断开 vs 异常断开 区分（主动断开不触发重连）
 *
 * 设计原则：
 *   本类只负责"连接 + 解析 + 重连"，不包含任何业务事件分发逻辑。
 *   业务层通过 onMessage 回调拿到原始 { id, event, data } 后自行处理。
 */

// ======================== 类型定义 ========================

/** 解析后的单条 SSE 事件 */
import { logger } from '@/utils/logger'

export interface SSEEvent {
  /** SSE id 字段（用于断点续传） */
  id: string
  /** SSE event 字段（默认 'message'） */
  event: string
  /** SSE data 字段（多行 data 用 '\n' 拼接） */
  data: string
}

/** SSEClient 配置项 */
export interface SSEClientOptions {
  /** 请求 URL */
  url: string
  /** HTTP 方法，默认 'GET' */
  method?: 'GET' | 'POST'
  /** 额外请求头（Authorization 等由 getToken 自动注入，无需在此重复） */
  headers?: Record<string, string>
  /** POST 请求体（对象会被 JSON.stringify） */
  body?: string | Record<string, unknown>
  /**
   * 收到解析后的 SSE 事件时触发。
   * 返回 false 可阻止 lastEventId 更新（用于心跳等非业务事件）。
   */
  onMessage: (event: SSEEvent) => void | boolean
  /** 连接建立成功时触发 */
  onOpen?: () => void
  /** 连接因错误断开时触发（主动 disconnect 不触发） */
  onError?: (error: Error) => void
  /** 连接关闭时触发（无论主动或被动） */
  onClose?: () => void
  /** 重连耗尽后触发（参数为已重试次数） */
  onReconnectExhausted?: (attempts: number) => void
  /** 最大重连次数，默认 5 */
  maxRetries?: number
  /** 初始重连延迟（毫秒），默认 1000。实际延迟 = initialRetryDelay * 2^attempt */
  initialRetryDelay?: number
  /** 退避上限（毫秒），默认 16000 */
  maxRetryDelay?: number
  /** 获取 Bearer token 的回调，每次连接时调用以支持 token 刷新 */
  getToken?: () => string | null
}

// ======================== SSE 协议解析 ========================

/** 空事件模板 */
function createEmptyEvent(): SSEEvent {
  return { id: '', event: 'message', data: '' }
}

/**
 * 解析一个 SSE 文本块（双换行分隔的一段），返回 SSEEvent 或 null（无有效 data）。
 * 完整遵循 https://html.spec.whatwg.org/multipage/server-sent-events.html 解析规则。
 */
function parseEventBlock(block: string): SSEEvent | null {
  const payload = createEmptyEvent()
  const dataLines: string[] = []

  for (const rawLine of block.split('\n')) {
    const line = rawLine.trimEnd()
    // 空行或注释行跳过
    if (!line || line.startsWith(':')) {
      continue
    }

    const colonIdx = line.indexOf(':')
    const field = colonIdx === -1 ? line : line.slice(0, colonIdx)
    const value = colonIdx === -1 ? '' : line.slice(colonIdx + 1).trimStart()

    if (field === 'id') {
      payload.id = value
    } else if (field === 'event') {
      payload.event = value || 'message'
    } else if (field === 'data') {
      dataLines.push(value)
    }
    // retry 字段本规范支持，但本客户端使用自定义退避策略，此处不处理
  }

  if (dataLines.length === 0) {
    return null
  }

  payload.data = dataLines.join('\n')
  return payload
}

// ======================== SSEClient 实现 ========================

export class SSEClient {
  private readonly options: Required<
    Pick<SSEClientOptions, 'method' | 'maxRetries' | 'initialRetryDelay' | 'maxRetryDelay'>
  > & SSEClientOptions

  private abortController: AbortController | null = null
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectAttempts = 0
  private lastEventId = ''
  private manuallyDisconnected = false
  private _connected = false

  constructor(options: SSEClientOptions) {
    this.options = {
      method: 'GET',
      maxRetries: 5,
      initialRetryDelay: 1000,
      maxRetryDelay: 16000,
      ...options,
    }
  }

  /** 当前是否处于连接状态 */
  get connected(): boolean {
    return this._connected
  }

  /** 当前已重连次数 */
  get retryCount(): number {
    return this.reconnectAttempts
  }

  /** 最近一次收到的 event id（用于外部断点续传） */
  get currentLastEventId(): string {
    return this.lastEventId
  }

  // -------------------- 公开方法 --------------------

  /** 建立连接（如已有连接会先关闭） */
  async connect(): Promise<void> {
    // 先清理旧连接
    await this.releaseResources()
    this.manuallyDisconnected = false
    this.abortController = new AbortController()

    await this.doConnect()
  }

  /** 主动断开连接（不会触发重连） */
  disconnect(): void {
    this.manuallyDisconnected = true
    this._connected = false
    this.clearReconnectTimer()
    this.abortController?.abort()
    this.abortController = null
    // reader.cancel() 会被 abort 信号级联触发，但保险起见显式释放
    void this.releaseReader()
    this.options.onClose?.()
  }

  /** 手动设置 lastEventId（用于外部恢复场景） */
  setLastEventId(id: string): void {
    this.lastEventId = id
  }

  // -------------------- 内部实现 --------------------

  private async doConnect(): Promise<void> {
    const { url, method, headers: extraHeaders, body, getToken } = this.options

    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      ...extraHeaders,
    }

    // 注入 Authorization（每次连接刷新，支持 token 续期场景）
    const token = getToken?.()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // POST 默认 Content-Type
    if (method === 'POST' && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json'
    }

    // Last-Event-ID 断点续传
    if (this.lastEventId) {
      headers['Last-Event-ID'] = this.lastEventId
    }

    // 序列化 body
    let serializedBody: string | undefined
    if (body !== undefined) {
      serializedBody = typeof body === 'string' ? body : JSON.stringify(body)
    }

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: serializedBody,
        signal: this.abortController?.signal,
      })

      if (!response.ok || !response.body) {
        const errMsg = `SSE 连接失败: HTTP ${response.status}`
        logger.warn(`[SSEClient] ${errMsg}`)
        this.options.onError?.(new Error(errMsg))
        this.scheduleReconnect()
        return
      }

      // 连接成功
      this._connected = true
      this.reconnectAttempts = 0
      this.options.onOpen?.()

      // 读取流
      this.reader = response.body.getReader()
      await this.readStream()
    } catch (err) {
      // AbortError 是主动断开，不触发重连/错误回调
      if (err instanceof DOMException && err.name === 'AbortError') {
        return
      }
      const error = err instanceof Error ? err : new Error('SSE 连接异常')
      logger.warn('[SSEClient] 连接异常:', error.message)
      this._connected = false
      this.options.onError?.(error)
      this.scheduleReconnect()
    }
  }

  private async readStream(): Promise<void> {
    if (!this.reader) {
      return
    }

    const decoder = new TextDecoder()
    let buffer = ''

    try {
      while (!this.manuallyDisconnected && this.reader) {
        const result = await this.reader.read()
        if (result.done) {
          break
        }

        buffer += decoder.decode(result.value, { stream: true })

        // SSE 协议：事件块之间用双换行分隔
        while (buffer.includes('\n\n')) {
          const separatorIndex = buffer.indexOf('\n\n')
          const block = buffer.slice(0, separatorIndex)
          buffer = buffer.slice(separatorIndex + 2)

          const event = parseEventBlock(block)
          if (event) {
            this.handleEvent(event)
          }
        }
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return
      }
      throw err
    }

    // 流正常结束
    this._connected = false
    await this.releaseReader()

    if (!this.manuallyDisconnected) {
      this.scheduleReconnect()
    }
  }

  private handleEvent(event: SSEEvent): void {
    // 更新 lastEventId（除非回调显式返回 false）
    const result = this.options.onMessage(event)
    if (result !== false && event.id) {
      this.lastEventId = event.id
    }
    // 成功收到消息，重置退避计数
    this.reconnectAttempts = 0
  }

  private scheduleReconnect(): void {
    if (this.manuallyDisconnected || this.reconnectTimer) {
      return
    }

    if (this.reconnectAttempts >= this.options.maxRetries) {
      logger.warn(
        `[SSEClient] 重连 ${this.options.maxRetries} 次失败，放弃`,
      )
      this.options.onReconnectExhausted?.(this.reconnectAttempts)
      return
    }

    const delay = Math.min(
      this.options.initialRetryDelay * Math.pow(2, this.reconnectAttempts),
      this.options.maxRetryDelay,
    )

    this.reconnectAttempts++
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      // 重建 AbortController（旧的已被 abort 或不可用）
      this.abortController = new AbortController()
      void this.doConnect()
    }, delay)
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  private async releaseReader(): Promise<void> {
    if (!this.reader) {
      return
    }
    const currentReader = this.reader
    this.reader = null
    try {
      await currentReader.cancel()
    } catch {
      // 连接已断开时 cancel 可能失败，忽略
    }
  }

  private async releaseResources(): Promise<void> {
    this.clearReconnectTimer()
    this.abortController?.abort()
    this.abortController = null
    this._connected = false
    await this.releaseReader()
  }
}
